import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const STATUS_COLORS = {
  trialing: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  active:   "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  canceled: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
  expired:  "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
  past_due: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
};

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "2-digit" }); }
  catch { return s; }
}

export default function AdminSubscriptions() {
  const [status, setStatus] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-subscriptions", status],
    queryFn: async () => (await api.get("/admin/subscriptions", {
      params: { status: status === "all" ? undefined : status, limit: 500 },
    })).data,
  });

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">Suscripciones</h1>
        <p className="text-sm text-muted-foreground mt-1">{items.length} suscripciones listadas.</p>
      </div>

      <div className="flex">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-subs-status" className="w-56">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="trialing">En prueba</SelectItem>
            <SelectItem value="active">Activas</SelectItem>
            <SelectItem value="past_due">Vencidas</SelectItem>
            <SelectItem value="canceled">Canceladas</SelectItem>
            <SelectItem value="expired">Expiradas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Sin suscripciones.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-white/40 dark:border-white/10">
                  <tr>
                    <th className="px-4 py-3">Usuario</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 hidden md:table-cell">Plan</th>
                    <th className="px-4 py-3 hidden md:table-cell">Asientos</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Trial hasta</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Periodo hasta</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.user_id} data-testid={`admin-sub-row-${s.user_id}`}
                        className="border-b border-white/30 dark:border-white/5 last:border-0 hover:bg-white/30 dark:hover:bg-white/5 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium truncate">{s.name || "—"}</div>
                        <div className="text-xs text-muted-foreground truncate">{s.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={STATUS_COLORS[s.subscription_status] || ""}>
                          {s.subscription_status || "—"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {s.plan
                          ? <span className="capitalize">{s.plan} · {s.billing_cycle} · {(s.currency || "").toUpperCase()}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">{s.seats || 1}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{fmtDate(s.trial_ends_at)}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{fmtDate(s.current_period_end)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
